import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatAmount } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Turnstile from "@/components/Turnstile";
import ProtectedLightbox from "@/components/ProtectedLightbox";
import PlanCard from "@/components/PlanCard";
import {
  Bed, Bath, Square, Layers, Car, Check, Download, ShoppingCart, ArrowLeft,
  BellRing, Clock, Maximize2, ChevronDown, ChevronUp, Home,
  Building2, FileText, FileCode, Printer, Zap,
  Ban, Copyright, Cloud, MessageSquare, PenTool,
  ArrowRight, Package as PackageIcon, Truck,
} from "lucide-react";
import { toast } from "sonner";
import { getCachedPlan, getCachedRelated, fetchPlan, fetchRelated, prefetchPlan } from "@/lib/planCache";
import { enabledTiers, enabledDownloadTiers, tierPrice, fromPrice, deliverablesForTier, PACKAGE_LABEL, PACKAGE_TAGLINE, tiersForChannel, isCustomizeTier } from "@/lib/packages";

const DRAWING_TYPE_LABELS = {
  floor_plan: "Floor Plan",
  foundation_plan: "Foundation Plan",
  roof_plan: "Roof Plan",
  site_plan: "Site Plan",
  exterior_elevation: "Exterior Elevation",
  interior_elevation: "Interior Elevation",
  building_section: "Building Section",
  construction_detail: "Construction Detail",
  electrical: "Electrical",
  plumbing: "Plumbing",
  hvac: "HVAC",
  schedule: "Schedule",
  other: "Other",
};

const FAQS = [
  {
    q: "What files do I actually receive after purchase?",
    a: "You receive an instant-download PDF Package containing every drawing sheet (floor plans, elevations, sections, roof, foundation, details) at full resolution and WITHOUT the preview watermark. CAD (DWG) files are available for many plans on request — contact us within 24 hours of purchase and we'll deliver the CAD set at no extra cost when it's available. BIM/Revit and printed sets are coming soon.",
  },
  {
    q: "Can I make changes to the plan?",
    a: "Yes. Every plan can be modified for your lot, family size, or local code. Use the 'Request Customization' button on this page or visit our Custom Design service — we quote free within 24 hours. Common modifications: mirroring, adding/removing rooms, adjusting garage size, changing roof pitch, adapting to different climate zones.",
  },
  {
    q: "Will these plans work with my local building code?",
    a: "Our plans are drawn to universal architectural standards. Structural, electrical, plumbing and energy code compliance varies by country / state / municipality — we'll adapt any plan to your local code as part of our Custom Design service, or your local architect / structural engineer can review before permit submission.",
  },
  {
    q: "How many times can I build this plan?",
    a: "Every purchase includes a single-build license — you can construct the house once, on a single lot. Additional builds require additional licenses. This is standard practice across the industry and it protects the years of design work behind every drawing.",
  },
  {
    q: "Can I share or resell the drawings?",
    a: "No. Redistribution, resale, or publishing of the drawings (physical or digital) is strictly prohibited. The plans remain copyright of Dream Home Engineers. You are free to share the plans with your contractor, engineer, and permit office for the specific build covered by your license.",
  },
  {
    q: "Do I get instant access after payment?",
    a: "Yes. As soon as your payment clears (typically under a minute), your download link is emailed to you and appears in your account under 'My Orders'. The link is signed and secure — only you can access it.",
  },
  {
    q: "What if I'm not happy with the plan?",
    a: "Because the plans are digital and delivered immediately, we do not offer refunds once the unwatermarked files are downloaded. However — we work with you until the plan fits your needs: free revisions on any minor issues, and you're only ever one email away from our team.",
  },
];

const WHATS_INCLUDED = [
  { Icon: FileText, title: "Detailed PDF Plans",
    body: "Every floor plan, elevation, section, roof plan, foundation, and construction detail — production-ready at full resolution." },
  { Icon: FileCode, title: "CAD Files (DWG)",
    body: "AutoCAD-compatible DWG files available on request for most plans — editable by your architect or engineer." },
  { Icon: Copyright, title: "Single-Build License",
    body: "Construct the design on one lot. Your license is registered to your name and delivered with the files." },
  { Icon: Download, title: "Instant Digital Delivery",
    body: "Access your unwatermarked files the moment your payment clears — no waiting, no shipping fees." },
  { Icon: PenTool, title: "Free Revision Support",
    body: "Minor tweaks, room mirroring, or local-code notes — we help you get the plan exactly right." },
  { Icon: Cloud, title: "Country-Code Adaptable",
    body: "Every plan is drawn to universal standards and can be adapted to your local building code by our team." },
];

const WHAT_YOU_RECEIVE = [
  { Icon: FileText, title: "PDF Package", status: "included", note: "Every sheet, full resolution, no watermark." },
  { Icon: FileCode, title: "CAD Package (DWG)", status: "on_request", note: "Ask within 24h of purchase — free where available." },
  { Icon: Printer, title: "Printed Sets", status: "coming", note: "Physical delivery of plot-sized sheets — coming soon." },
  { Icon: Building2, title: "BIM / Revit Models", status: "coming", note: "3D BIM models for advanced workflows — in production." },
  { Icon: Zap, title: "Instant Download", status: "included", note: "Delivered by email + account within minutes of payment." },
];

const LICENSE_ROWS = [
  { Icon: Home, title: "Single-Build License", body: "Build the design once, on one lot. Additional builds require additional licenses." },
  { Icon: Ban, title: "No Redistribution", body: "You may share the files with your contractor, engineer, and permit office — not publicly." },
  { Icon: Ban, title: "No Resale", body: "The drawings, in whole or in part, cannot be resold or repackaged." },
  { Icon: Copyright, title: "Copyright Protected", body: "All plans remain copyright © Dream Home Engineers. Your license is a right to build, not to reproduce." },
  { Icon: Cloud, title: "Digital-First Delivery", body: "PDFs and (where available) CAD delivered digitally — instant, signed, and download-history tracked." },
];

// -----------------------------------------------------------------
// Per-plan SEO helpers — set/patch <meta>, <link rel="canonical">, and
// inject Product JSON-LD. Each returns a cleanup function that reverts
// or removes the injected element on unmount.
// -----------------------------------------------------------------
function setMetaTag(attr, key, value) {
  if (value == null || value === "") return null;
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", String(value));
  return () => {
    if (created) el.parentNode && el.parentNode.removeChild(el);
    else if (prev != null) el.setAttribute("content", prev);
  };
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  const created = !el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  return () => {
    if (created) el.parentNode && el.parentNode.removeChild(el);
    else if (prev != null) el.setAttribute("href", prev);
  };
}

function injectJsonLd(id, data) {
  let el = document.getElementById(id);
  const created = !el;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return () => {
    if (created) el.parentNode && el.parentNode.removeChild(el);
  };
}

const PlanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currency, addToCart, cart, user } = useApp();
  // Hydrate synchronously from module cache so back-nav / re-visit is INSTANT.
  // Background refetch keeps the data fresh (SWR). This eliminates the
  // blank-page flash between marketplace → plan navigation.
  const [plan, setPlan] = useState(() => getCachedPlan(id));
  const [related, setRelated] = useState(() => getCachedRelated(id) || []);
  const [heroIndex, setHeroIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStart, setLightboxStart] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notified, setNotified] = useState(false);
  const [tsToken, setTsToken] = useState("");
  const [showStickyBar, setShowStickyBar] = useState(false);
  // Iter48 — Selected package tier for buy panel. Defaults to lowest enabled
  // tier when the plan loads. Falls back to "pdf" for legacy plans.
  const [selectedTier, setSelectedTier] = useState("pdf");
  const heroRef = useRef(null);
  const onTsToken = useCallback((t) => setTsToken(t), []);
  // Filter chip — 'All' by default; other keys narrow the gallery.
  // Categories: renders, videos, floor_plans, elevations, sections, other_details.
  const [galleryFilter, setGalleryFilter] = useState("all");

  // Iter60A — Marketplace context. `?channel=floor_plan` (from /floor-plans)
  // switches PDP to floor-plan tiers/pricing. Absent → premium (baseline).
  // Guarded against manual URL tampering: if the plan doesn't support the
  // requested channel we silently fall back to premium.
  const rawChannel = (searchParams.get("channel") || "premium").toLowerCase();
  const channel = useMemo(() => {
    const sc = (plan?.sales_channel || "premium").toLowerCase();
    if (rawChannel === "floor_plan" && (sc === "floor_plan" || sc === "both")) return "floor_plan";
    return "premium";
  }, [rawChannel, plan?.sales_channel]);
  // If URL asks for a channel the plan doesn't offer, drop the query param so
  // the URL stays honest and the user can bookmark/share cleanly. Guarded so
  // it never runs before `plan` has hydrated (otherwise the param would be
  // stripped during the initial null-plan render).
  useEffect(() => {
    if (!plan) return;
    if (rawChannel === "floor_plan" && channel !== "floor_plan") {
      const next = new URLSearchParams(searchParams);
      next.delete("channel");
      setSearchParams(next, { replace: true });
    }
  }, [plan, rawChannel, channel, searchParams, setSearchParams]);

  // Load plan (cache-first) — background refetch keeps data fresh.
  useEffect(() => {
    // Reset hydration when id changes.
    const cached = getCachedPlan(id);
    if (cached) setPlan(cached);
    else setPlan(null);
    fetchPlan(id)
      .then((data) => setPlan(data))
      .catch(() => {
        toast.error("Plan not available");
        navigate("/plans");
      });
  }, [id, navigate]);

  // Iter48 + Iter60A — When the plan/packages/channel change, snap the
  // selected tier to the cheapest enabled tier for the ACTIVE channel.
  useEffect(() => {
    if (!plan) return;
    const tiers = enabledTiers(plan, channel);
    if (tiers.length && !tiers.includes(selectedTier)) {
      setSelectedTier(tiers[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.id, plan?.packages, channel]);

  // Per-plan SEO — dynamic <title>, meta description, OG/Twitter tags,
  // canonical link, and Product JSON-LD. Cleans up injected tags on
  // unmount so navigating to another route doesn't leak stale metadata.
  useEffect(() => {
    if (!plan) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = `${origin}/plans/${plan.id}`;
    const heroImg = plan.images?.[0] || plan.floor_plan_images?.[0] || `${origin}/og-default.jpg`;
    const styleLabel = plan.style ? `${plan.style} ` : "";
    const spec = [
      plan.bedrooms != null ? `${plan.bedrooms} bed` : null,
      plan.bathrooms != null ? `${plan.bathrooms} bath` : null,
      typeof plan.sqft === "number" ? `${plan.sqft.toLocaleString()} sqft` : null,
    ].filter(Boolean).join(" · ");
    const title = `${plan.title}${spec ? " — " + spec : ""} | Dream Home Plans`;
    // Iter55f — SEO fallback chain for meta description:
    // manual short_description > manual description > approved AI meta_description
    // > approved AI short_description > generic fallback. Manual copy always wins.
    const desc = (
      plan.short_description
      || plan.description
      || plan.ai_meta_description
      || plan.ai_short_description
      || `${styleLabel}house plan by Dream Home Engineers. ${spec}`
    ).slice(0, 320);
    const priceUsd = typeof plan.price_usd === "number" ? plan.price_usd : null;

    document.title = title;
    const cleanups = [];
    cleanups.push(setMetaTag("name", "description", desc));
    // Iter55f — approved AI search keywords flow to <meta name="keywords">.
    if (Array.isArray(plan.ai_search_keywords) && plan.ai_search_keywords.length > 0) {
      cleanups.push(setMetaTag("name", "keywords", plan.ai_search_keywords.join(", ")));
    }
    cleanups.push(setMetaTag("property", "og:title", plan.title));
    cleanups.push(setMetaTag("property", "og:description", desc));
    cleanups.push(setMetaTag("property", "og:type", "product"));
    cleanups.push(setMetaTag("property", "og:url", url));
    cleanups.push(setMetaTag("property", "og:image", heroImg));
    cleanups.push(setMetaTag("name", "twitter:card", "summary_large_image"));
    cleanups.push(setMetaTag("name", "twitter:title", plan.title));
    cleanups.push(setMetaTag("name", "twitter:description", desc));
    cleanups.push(setMetaTag("name", "twitter:image", heroImg));
    cleanups.push(setCanonical(url));

    // Product JSON-LD for rich search results.
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": plan.title,
      "sku": plan.project_id || plan.id,
      "description": desc,
      "image": (plan.images || []).slice(0, 4),
      "category": plan.style ? `${plan.style} House Plans` : "House Plans",
      "brand": { "@type": "Brand", "name": "Dream Home Engineers" },
      "url": url,
      "additionalProperty": [
        plan.bedrooms != null && { "@type": "PropertyValue", "name": "Bedrooms", "value": plan.bedrooms },
        plan.bathrooms != null && { "@type": "PropertyValue", "name": "Bathrooms", "value": plan.bathrooms },
        typeof plan.sqft === "number" && { "@type": "PropertyValue", "name": "Square Feet", "value": plan.sqft },
        plan.stories != null && { "@type": "PropertyValue", "name": "Stories", "value": plan.stories },
        plan.garage != null && { "@type": "PropertyValue", "name": "Garage Bays", "value": plan.garage },
      ].filter(Boolean),
    };
    if (priceUsd != null) {
      jsonLd.offers = {
        "@type": "Offer",
        "price": priceUsd,
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "url": url,
      };
    }
    cleanups.push(injectJsonLd("plan-detail-jsonld", jsonLd));

    return () => cleanups.forEach((fn) => fn && fn());
  }, [plan]);


  useEffect(() => {
    if (!id) return;
    const cachedRel = getCachedRelated(id);
    if (cachedRel) setRelated(cachedRel);
    fetchRelated(id, 4).then((r) => setRelated(r || []));
  }, [id]);

  // Adjacent-plan prefetch — warm the cache for anything the user might
  // click next (related plans). Turns the next plan click into an instant
  // navigation for near-100% of user flows.
  useEffect(() => {
    if (!related?.length) return;
    // Delay a beat so we don't compete with the current plan's paint.
    const t = setTimeout(() => {
      related.slice(0, 4).forEach((p) => {
        if (p?.id) prefetchPlan(p.id, (p.images_hero || p.images || [])[0]);
      });
    }, 400);
    return () => clearTimeout(t);
  }, [related]);

  // Sticky bar visibility
  useEffect(() => {
    const onScroll = () => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      setShowStickyBar(rect.bottom < 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [plan]);

  // Coming-soon countdown
  const [countdown, setCountdown] = useState(null);
  useEffect(() => {
    if (!plan?.launch_date) return;
    const tick = () => {
      const t = new Date(plan.launch_date).getTime() - Date.now();
      if (t <= 0) return setCountdown({ d: 0, h: 0, m: 0, s: 0, done: true });
      setCountdown({
        d: Math.floor(t / 86400000),
        h: Math.floor((t % 86400000) / 3600000),
        m: Math.floor((t % 3600000) / 60000),
        s: Math.floor((t % 60000) / 1000),
        done: false,
      });
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [plan]);

  // Unified gallery — renders + videos + drawings, one continuous list.
  // Filter chips (default 'all') narrow the same list to a single category.
  // Order: renders → videos → floor plans → elevations → sections → other.
  const allMedia = useMemo(() => {
    if (!plan) return [];
    const renderings = (plan.images || []).map((u, i) => ({
      url: u,
      url_thumb: (plan.images_thumb || [])[i] || u,
      url_zoom: (plan.images_zoom || [])[i] || u,
      type: "rendering",
      group: "renders",
      label: `Exterior Render ${i + 1}`,
      fit: "cover",
    }));
    const videos = (plan.videos || []).map((v, i) => ({
      url: v.url,
      poster_url: v.poster_url || v.poster || "",
      source: v.source || "file",
      mime: v.mime,
      duration_s: v.duration_s,
      type: "video",
      group: "videos",
      label: v.label || `Walkthrough ${i + 1}`,
      fit: "cover",
      isVideo: true,
    }));
    const drawings = (plan.technical_drawings || []).map((d, i) => {
      const t = d.type || "floor_plan";
      let group = "other_details";
      if (["floor_plan", "foundation_plan", "roof_plan", "site_plan"].includes(t)) group = "floor_plans";
      else if (["exterior_elevation", "interior_elevation"].includes(t)) group = "elevations";
      else if (["building_section"].includes(t)) group = "sections";
      return {
        url: d.url,
        url_thumb: d.url_thumb || d.url,
        url_zoom: d.url_zoom || d.url,
        type: t,
        group,
        label: d.label || `${DRAWING_TYPE_LABELS[t] || "Drawing"} ${i + 1}`,
        fit: "contain",
      };
    });
    // Sort drawings so each group stays contiguous in the unified stream.
    const groupOrder = ["floor_plans", "elevations", "sections", "other_details"];
    drawings.sort((a, b) => groupOrder.indexOf(a.group) - groupOrder.indexOf(b.group));
    return [...renderings, ...videos, ...drawings];
  }, [plan]);

  // Filter chip definitions — dynamic (only shown if group has ≥1 item).
  const galleryGroups = useMemo(() => {
    const counts = new Map();
    for (const m of allMedia) counts.set(m.group, (counts.get(m.group) || 0) + 1);
    const defs = [
      { key: "renders", label: "Renders" },
      { key: "videos", label: "Videos" },
      { key: "floor_plans", label: "Floor Plans" },
      { key: "elevations", label: "Elevations" },
      { key: "sections", label: "Sections" },
      { key: "other_details", label: "Other Details" },
    ];
    return defs.filter((g) => (counts.get(g.key) || 0) > 0).map((g) => ({ ...g, count: counts.get(g.key) }));
  }, [allMedia]);

  // Filtered view — 'all' shows everything continuously; others narrow.
  const allImages = useMemo(() => {
    if (galleryFilter === "all") return allMedia;
    return allMedia.filter((m) => m.group === galleryFilter);
  }, [allMedia, galleryFilter]);

  // Reset chip selection if the current filter has no items after a plan change.
  useEffect(() => {
    if (galleryFilter !== "all" && !galleryGroups.some((g) => g.key === galleryFilter)) {
      setGalleryFilter("all");
    }
  }, [galleryGroups, galleryFilter]);

  // Summary of drawing-type coverage for the "sample plan set" band.
  const sampleCoverage = useMemo(() => {
    const groups = new Map();
    for (const im of allMedia) {
      const key = im.type === "rendering" ? "rendering" : (im.type || "other");
      groups.set(key, (groups.get(key) || 0) + 1);
    }
    return Array.from(groups.entries()).map(([type, count]) => ({
      type,
      count,
      label: type === "rendering" ? "Exterior Renders" : (type === "video" ? "Walkthrough Videos" : (DRAWING_TYPE_LABELS[type] || type)),
    }));
  }, [allMedia]);

  // Snap hero back to first slot if the gallery shrinks below current index.
  useEffect(() => {
    if (heroIndex >= allImages.length && allImages.length > 0) setHeroIndex(0);
  }, [allImages, heroIndex]);

  // Priority 2 — aggressive prefetch: hero (eager) + next 3 gallery thumbs
  // + zoom variant of the CURRENT hero so lightbox opens instant.
  useEffect(() => {
    if (!plan || !allImages.length) return;
    const preloadOne = (url, as = "image") => {
      if (!url) return;
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = as;
      link.href = url;
      link.dataset.dhePreload = "1";
      document.head.appendChild(link);
    };
    // Preload up to 8 hero variants so clicking any thumb — including
    // floor plans that sit later in the unified gallery — is instant.
    // Renderings sit first in allImages; drawings follow. On a typical
    // plan (3 renders + 5 drawings = 8 items) this covers everything.
    allImages.slice(0, 8).forEach((im) => im?.url && preloadOne(im.url));
    // Preload zoom variant of the current hero for snappy lightbox
    const cur = allImages[heroIndex];
    if (cur?.url_zoom && cur.url_zoom !== cur.url) preloadOne(cur.url_zoom);
    return () => {
      document.head.querySelectorAll('link[data-dhe-preload="1"]').forEach((n) => n.remove());
    };
  }, [plan, allImages, heroIndex]);

  if (!plan) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-32 text-center text-muted-foreground">Loading plan…</div>
      </Layout>
    );
  }

  const isComingSoon = plan.status === "draft" && plan.coming_soon;
  const inCart = cart.find((c) => c.id === plan.id);
  const heroImg = allImages[heroIndex];

  const stopMediaEvents = (e) => { e.preventDefault(); return false; };
  // Open lightbox at a specific gallery position — but skip videos, since
  // Lightbox is image-only. Remap the allImages index → filtered-images index.
  const openLightboxAt = (idx = 0) => {
    const target = allImages[idx];
    if (!target || target.isVideo) return;
    const filtered = allImages.filter((m) => !m.isVideo);
    const remapped = filtered.findIndex((m) => m.url === target.url && m.label === target.label);
    setLightboxStart(remapped >= 0 ? remapped : 0);
    setLightboxOpen(true);
  };

  const handleAdd = () => { addToCart(plan, selectedTier); toast.success("Added to cart", { description: `${plan.title} · ${PACKAGE_LABEL[selectedTier]} Package` }); };
  const handleBuyNow = () => { addToCart(plan, selectedTier); navigate("/cart"); };
  const handleRequestCustom = () => navigate(`/custom-design?ref=${encodeURIComponent(plan.id)}`);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!notifyEmail.trim()) return;
    setNotifyLoading(true);
    try {
      await api.post(`/plans/${plan.id}/notify`, { email: notifyEmail.trim(), name: user?.name || "" });
      setNotified(true);
      toast.success("You're on the list", { description: "We'll email you the moment this plan goes live." });
    } catch (err) {
      toast.error("Could not subscribe", { description: err.response?.data?.detail || "Try again." });
    } finally { setNotifyLoading(false); }
  };

  const purchaseCta = !isComingSoon ? {
    message: "Purchase this plan to access high-resolution files and the complete drawing set.",
    onBuyNow: handleBuyNow,
    onAddToCart: inCart ? null : handleAdd,
    onRequestCustom: handleRequestCustom,
  } : null;

  return (
    <Layout>
      {/* Sticky mobile buy bar */}
      {!isComingSoon && showStickyBar && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-border shadow-2xl px-4 py-3 flex items-center justify-between gap-3 lg:hidden" data-testid="sticky-buy-bar">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{plan.title}</div>
            <div className="font-display text-xl">
              {(() => {
                const from = fromPrice(plan, currency, channel);
                return from > 0 ? formatAmount(from, currency) : "Enquiry";
              })()}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAdd} disabled={!!inCart} variant="outline" size="sm" className="rounded-none" data-testid="sticky-add-cart">
              <ShoppingCart size={14} className="mr-1" /> {inCart ? "In cart" : "Add"}
            </Button>
            <Button onClick={handleBuyNow} size="sm" className="rounded-none bg-accent hover:bg-accent/90" data-testid="sticky-buy-now">
              Buy now
            </Button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-14">
        <Link to="/plans" className="inline-flex items-center gap-2 overline text-accent hover:underline mb-8" data-testid="back-to-plans">
          <ArrowLeft size={14} /> Back to collection
        </Link>

        {/* HERO: Tabbed gallery + sticky buy panel */}
        <div ref={heroRef} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
          {/* Left — unified gallery (no category tabs) */}
          <div className="lg:col-span-7">
            {/* Filter chips — 'All' default. Chips appear only if >=1 group has items.
                Hidden entirely when only ONE category exists (nothing to filter). */}
            {galleryGroups.length > 1 && (
              <div className="flex flex-wrap gap-1.5 mb-3 border-b border-border pb-3" data-testid="gallery-filter-chips">
                <button
                  onClick={() => setGalleryFilter("all")}
                  data-testid="chip-all"
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold transition border ${galleryFilter === "all" ? "border-accent text-accent bg-accent/5" : "border-border text-muted-foreground hover:text-foreground"}`}
                >
                  All <span className="font-mono ml-1 opacity-60">{allMedia.length}</span>
                </button>
                {galleryGroups.map((g) => (
                  <button
                    key={g.key}
                    onClick={() => setGalleryFilter(g.key)}
                    data-testid={`chip-${g.key}`}
                    className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold transition border ${galleryFilter === g.key ? "border-accent text-accent bg-accent/5" : "border-border text-muted-foreground hover:text-foreground"}`}
                  >
                    {g.label} <span className="font-mono ml-1 opacity-60">{g.count}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Type + label bar */}
            {heroImg && (
              <div className="flex items-baseline justify-between mb-2 px-1" data-testid="hero-type-bar">
                <div>
                  <div className="overline text-accent">{heroImg.isVideo ? "Walkthrough Video" : (DRAWING_TYPE_LABELS[heroImg.type] || heroImg.type)}</div>
                  <div className="text-base font-semibold mt-0.5" data-testid="hero-label">{heroImg.label}</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
                  {heroIndex + 1} / {allImages.length}
                </div>
              </div>
            )}

            {/* Hero image / video */}
            {heroImg?.isVideo ? (
              <div
                className="group relative w-full aspect-[16/9] bg-neutral-900 overflow-hidden border border-border"
                data-testid="hero-video-container"
              >
                <video
                  key={heroImg.url}
                  src={heroImg.url}
                  poster={heroImg.poster_url || undefined}
                  controls
                  preload="metadata"
                  playsInline
                  controlsList="nodownload"
                  onContextMenu={stopMediaEvents}
                  data-protected="true"
                  data-testid="hero-video"
                  className="w-full h-full object-contain bg-neutral-900"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => heroImg && openLightboxAt(heroIndex)}
                className="group relative w-full aspect-[4/3] bg-neutral-100 overflow-hidden border border-border block"
                data-testid="hero-image-btn"
                aria-label="Open in protected viewer"
              >
                {heroImg && (
                  <img
                    src={heroImg.url}
                    alt={heroImg.label}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    draggable={false}
                    onDragStart={stopMediaEvents}
                    onContextMenu={stopMediaEvents}
                    data-protected="true"
                    data-testid="hero-image"
                    className={`w-full h-full ${heroImg.fit === "cover" ? "object-cover" : "object-contain p-2"} select-none pointer-events-none`}
                  />
                )}
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/0 group-hover:from-black/25 transition" />
                <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition bg-black/70 backdrop-blur text-white text-xs px-2.5 py-1.5 rounded flex items-center gap-1.5">
                  <Maximize2 size={12} /> Open protected viewer
                </div>
              </button>
            )}

            {/* Prev/Next across the entire unified gallery */}
            {allImages.length > 1 && (
              <div className="flex items-center justify-between mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setHeroIndex((i) => (i - 1 + allImages.length) % allImages.length)}
                  className="rounded-none"
                  data-testid="hero-prev"
                >
                  <ArrowLeft size={14} className="mr-1" /> Previous
                </Button>
                <div className="text-xs text-muted-foreground">
                  {heroIndex + 1} of {allImages.length}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setHeroIndex((i) => (i + 1) % allImages.length)}
                  className="rounded-none"
                  data-testid="hero-next"
                >
                  Next <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
            )}

            {/* Continuous thumb rail — one grid, all images, no tabs */}
            {allImages.length > 1 && (
              <div
                className="mt-4 grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2 max-h-[440px] overflow-y-auto pr-1"
                data-testid="hero-thumb-rail"
              >
                {allImages.map((img, idx) => (
                  <button
                    key={`${img.url}-${idx}`}
                    onClick={() => setHeroIndex(idx)}
                    onDoubleClick={() => !img.isVideo && openLightboxAt(idx)}
                    data-testid={`thumb-${idx}`}
                    title={`${img.isVideo ? "Video" : (DRAWING_TYPE_LABELS[img.type] || img.type)} — ${img.label}`}
                    className={`relative aspect-square overflow-hidden border-2 transition-fast bg-neutral-100 ${heroIndex === idx ? "border-accent" : "border-transparent hover:border-neutral-300"}`}
                  >
                    <img
                      src={img.isVideo ? (img.poster_url || "") : img.url_thumb}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                      onDragStart={stopMediaEvents}
                      onContextMenu={stopMediaEvents}
                      data-protected="true"
                      className={`w-full h-full ${img.fit === "cover" || img.isVideo ? "object-cover" : "object-contain p-1"} select-none pointer-events-none`}
                    />
                    {img.isVideo && (
                      <span className="absolute inset-0 flex items-center justify-center bg-black/25 pointer-events-none">
                        <span className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow">
                          <span className="border-y-[6px] border-y-transparent border-l-[9px] border-l-neutral-900 ml-0.5" />
                        </span>
                      </span>
                    )}
                    {!img.isVideo && img.type !== "rendering" && (
                      <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] uppercase tracking-widest px-1 py-0.5 leading-none text-center truncate">
                        {DRAWING_TYPE_LABELS[img.type] || img.type}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right — buy panel */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="overline text-accent" data-testid="plan-style">{plan.style}</div>
              {plan.project_id && (
                <span
                  data-testid="plan-project-id"
                  className="font-mono text-[10px] uppercase tracking-widest border border-border rounded-sm px-1.5 py-0.5 text-muted-foreground bg-muted/40"
                  title="Permanent Project ID — quote this in any correspondence"
                >
                  {plan.project_id}
                </span>
              )}
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl mt-3 leading-[0.95] tracking-tighter" data-testid="plan-title">
              {plan.title}
            </h1>
            <p className="mt-6 text-lg text-muted-foreground italic font-display">
              {plan.short_description}
            </p>

            <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4 border-y border-border py-6" data-testid="plan-specs">
              <Spec icon={Bed} value={plan.bedrooms} label="Bedrooms" />
              <Spec icon={Bath} value={plan.bathrooms} label="Bathrooms" />
              <Spec icon={Square} value={plan.sqft?.toLocaleString?.()} label="Sq. Ft." />
              <Spec icon={Layers} value={plan.stories} label="Stories" />
              <Spec icon={Car} value={plan.garage} label="Garage bays" />
            </div>

            <div className="mt-8" data-testid="plan-price-block">
              {(() => {
                // Iter60A — package selector filters by ACTIVE channel.
                const channelTiers = tiersForChannel(channel);
                const availableTiers = enabledTiers(plan, channel);
                const downloadable = enabledDownloadTiers(plan, channel);
                if (isComingSoon) return null;

                // No purchasable tier enabled for this channel → mark unavailable.
                // Customize alone doesn't count as purchasable.
                if (!downloadable.length) {
                  return (
                    <>
                      <div className="overline text-muted-foreground">Availability</div>
                      <div className="font-display text-2xl text-muted-foreground mt-2" data-testid="plan-unavailable">
                        {channel === "floor_plan"
                          ? "Floor plan package not yet configured"
                          : "Not yet available for purchase"}
                      </div>
                      {availableTiers.includes("fp_customize") && (
                        <div className="mt-2 text-sm text-muted-foreground">
                          Request a custom version of this plan — our team will get back within 24 hours.
                        </div>
                      )}
                    </>
                  );
                }

                // Package-driven pricing UI (customize tile shown but not priced).
                const priceNow = isCustomizeTier(selectedTier) ? 0 : tierPrice(plan, selectedTier, currency);
                return (
                  <>
                    <div className="overline text-muted-foreground flex items-center gap-2">
                      <PackageIcon size={12} /> Choose your package
                    </div>
                    <div className={`mt-3 grid gap-2 ${channelTiers.length === 4 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`} data-testid="package-selector">
                      {channelTiers.map((t) => {
                        const enabled = availableTiers.includes(t);
                        const active = selectedTier === t;
                        const customize = isCustomizeTier(t);
                        const price = customize ? 0 : tierPrice(plan, t, currency);
                        return (
                          <button
                            key={t}
                            type="button"
                            disabled={!enabled}
                            onClick={() => setSelectedTier(t)}
                            data-testid={`pkg-tier-${t}`}
                            className={`border p-3 text-left transition-fast ${
                              active
                                ? "border-primary bg-primary text-primary-foreground"
                                : enabled
                                  ? "border-border hover:border-foreground"
                                  : "border-border/60 opacity-40 cursor-not-allowed"
                            }`}
                          >
                            <div className={`overline text-[10px] ${active ? "text-primary-foreground" : "text-muted-foreground"}`}>
                              {PACKAGE_LABEL[t]}
                            </div>
                            <div className={`font-display text-lg mt-1 ${active ? "" : "text-foreground"}`}>
                              {!enabled ? "N/A" : customize ? "Enquiry" : formatAmount(price, currency)}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-4">
                      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                        {isCustomizeTier(selectedTier) ? "Request" : "Total"}
                      </div>
                      <div className="font-display text-5xl text-primary mt-1" data-testid="detail-price">
                        {isCustomizeTier(selectedTier) ? "Enquiry" : formatAmount(priceNow, currency)}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {PACKAGE_TAGLINE[selectedTier]}
                        {(selectedTier === "cad" || selectedTier === "revit" || selectedTier === "fp_cad" || selectedTier === "fp_revit") && (
                          <span className="ml-2 inline-flex items-center gap-1 text-accent">
                            <Truck size={11} /> Manual delivery by our team
                          </span>
                        )}
                      </div>
                      {plan.packages?.[selectedTier]?.description && (
                        <p className="mt-2 text-sm text-muted-foreground">
                          {plan.packages[selectedTier].description}
                        </p>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>

            {isComingSoon ? (
              <div className="mt-8 border border-accent/40 bg-accent/5 p-6" data-testid="coming-soon-block">
                <div className="flex items-center gap-2 overline text-accent">
                  <BellRing size={14} /> Coming soon
                </div>
                <h3 className="font-display text-2xl mt-2">Be first to know when this plan drops</h3>
                {plan.launch_date && (
                  <div className="mt-3 text-sm text-muted-foreground flex items-center gap-2">
                    <Clock size={12} /> Expected launch: {new Date(plan.launch_date).toLocaleDateString(undefined, { dateStyle: "long" })}
                  </div>
                )}
                {countdown && !countdown.done && (
                  <div className="mt-3 grid grid-cols-4 gap-2 max-w-xs" data-testid="coming-soon-countdown">
                    {["d", "h", "m", "s"].map((k) => (
                      <div key={k} className="border border-border bg-background p-2 text-center">
                        <div className="font-display text-2xl">{String(countdown[k]).padStart(2, "0")}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k === "d" ? "days" : k === "h" ? "hrs" : k === "m" ? "min" : "sec"}</div>
                      </div>
                    ))}
                  </div>
                )}
                {notified ? (
                  <div className="mt-4 text-sm text-accent" data-testid="notify-confirmed">✓ We&apos;ll email you the moment it goes live.</div>
                ) : (
                  <form onSubmit={handleNotify} className="mt-4 space-y-3" data-testid="notify-me-form">
                    <div className="flex gap-2">
                      <Input
                        data-testid="notify-me-email" type="email" required
                        value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)}
                        placeholder="you@example.com" className="rounded-none h-11"
                      />
                      <Button data-testid="notify-me-submit" type="submit" disabled={notifyLoading} className="rounded-none h-11 bg-accent hover:bg-accent/90">
                        {notifyLoading ? "…" : "Notify me"}
                      </Button>
                    </div>
                    <Turnstile onToken={onTsToken} action="notify-me" />
                  </form>
                )}
              </div>
            ) : (
              (() => {
                const tiers = enabledTiers(plan, channel);
                const downloadable = enabledDownloadTiers(plan, channel);
                if (!downloadable.length) {
                  return (
                    <div className="mt-8 border border-border bg-background p-6 text-center text-sm text-muted-foreground" data-testid="not-for-sale-block">
                      {tiers.includes("fp_customize")
                        ? "No standard package available — request a custom version below."
                        : "This plan is not yet available for purchase on this marketplace."}
                      <div className="mt-4">
                        <Button data-testid="request-custom-btn" onClick={handleRequestCustom} className="rounded-none bg-accent hover:bg-accent/90 h-12 px-6">
                          <MessageSquare size={14} className="mr-2" /> Request customization
                        </Button>
                      </div>
                    </div>
                  );
                }
                const customizeSelected = isCustomizeTier(selectedTier);
                return (
              <div className="mt-8 flex flex-col gap-3" data-testid="buy-panel">
                {customizeSelected ? (
                  <Button data-testid="request-custom-cta" size="lg" onClick={handleRequestCustom}
                    className="rounded-none bg-accent hover:bg-accent/90 h-14 px-8 w-full">
                    <MessageSquare size={16} className="mr-2" /> Request customization enquiry
                  </Button>
                ) : (
                  <Button data-testid="buy-now-btn" size="lg" onClick={handleBuyNow}
                    className="rounded-none bg-accent hover:bg-accent/90 h-14 px-8 w-full">
                    {selectedTier === "pdf" || selectedTier === "fp_pdf"
                      ? "Buy now — instant download"
                      : `Buy ${PACKAGE_LABEL[selectedTier]} Package`}
                  </Button>
                )}
                <div className="flex gap-3">
                  {!customizeSelected && (
                    <Button data-testid="add-to-cart-btn" size="lg" variant="outline" onClick={handleAdd}
                      className="rounded-none h-12 flex-1 border-foreground">
                      <ShoppingCart size={16} className="mr-2" />
                      {inCart ? (inCart.package_tier === selectedTier ? "In cart" : "Update package") : "Add to cart"}
                    </Button>
                  )}
                  <Button data-testid="request-custom-btn" size="lg" variant="outline" onClick={handleRequestCustom}
                    className={`rounded-none h-12 border-foreground ${customizeSelected ? "w-full" : "flex-1"}`}>
                    <MessageSquare size={16} className="mr-2" />
                    Customize
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground text-center pt-2">
                  {customizeSelected
                    ? "Our team will contact you within 24 hours."
                    : (selectedTier === "pdf" || selectedTier === "fp_pdf")
                      ? "Secure checkout · Instant PDF delivery · Free minor revisions"
                      : "Secure checkout · Editable files delivered manually within our committed timeframe"}
                </div>
              </div>
                );
              })()
            )}

            {plan.pdf_url && (
              <a href={plan.pdf_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm text-accent hover:underline" data-testid="download-preview">
                <Download size={14} /> Preview PDF
              </a>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7">
            <div className="overline text-accent">About this plan</div>
            <h2 className="font-display text-4xl mt-3 mb-8">The design</h2>
            <div className="prose max-w-none text-lg text-muted-foreground whitespace-pre-line leading-relaxed" data-testid="plan-description">
              {plan.description}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="overline text-accent">Key features</div>
            <h2 className="font-display text-4xl mt-3 mb-8">Highlights</h2>
            <ul className="space-y-3">
              {(plan.features || []).map((f, i) => (
                <li key={f} className="flex items-start gap-3" data-testid={`feature-${i}`}>
                  <Check size={18} className="text-accent shrink-0 mt-1" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Iter55f — AI Content Manager · Only approved AI fields appear
            below. Manual copy always wins for overlapping fields; the
            content in this block is exclusive to the AI pipeline (extra
            architectural highlights, buyer-fit tags, room schedule from
            floor plan vision). Nothing here renders unless the admin
            ticked "Approve" AND saved on the plan editor. */}
        {(Array.isArray(plan.ai_highlights) && plan.ai_highlights.length > 0 ||
          Array.isArray(plan.ai_best_for) && plan.ai_best_for.length > 0 ||
          plan.ai_suitable_family_size ||
          Array.isArray(plan.ai_room_schedule) && plan.ai_room_schedule.length > 0 ||
          Array.isArray(plan.ai_tags) && plan.ai_tags.length > 0) && (
          <section className="mt-24" data-testid="plan-ai-content">
            <div className="max-w-3xl">
              <div className="overline text-accent">Details from the design</div>
              <h2 className="font-display text-4xl md:text-5xl mt-3">A closer look</h2>
            </div>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
              {Array.isArray(plan.ai_highlights) && plan.ai_highlights.length > 0 && (
                <div className="lg:col-span-7" data-testid="plan-ai-highlights">
                  <div className="overline text-accent">Architectural highlights</div>
                  <ul className="mt-4 space-y-3">
                    {plan.ai_highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check size={18} className="text-accent shrink-0 mt-1" />
                        <span className="text-lg text-neutral-800">{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="lg:col-span-5 space-y-8">
                {(Array.isArray(plan.ai_best_for) && plan.ai_best_for.length > 0 || plan.ai_suitable_family_size) && (
                  <div data-testid="plan-ai-suitable-for">
                    <div className="overline text-accent">Suitable for</div>
                    {Array.isArray(plan.ai_best_for) && plan.ai_best_for.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {plan.ai_best_for.map((tag) => (
                          <span key={tag} className="text-sm px-3 py-1 border border-border bg-background" data-testid={`plan-ai-best-for-${tag}`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {plan.ai_suitable_family_size && (
                      <div className="mt-3 text-sm text-muted-foreground">
                        Family size: <span className="text-foreground font-medium">{plan.ai_suitable_family_size}</span>
                      </div>
                    )}
                  </div>
                )}

                {Array.isArray(plan.ai_tags) && plan.ai_tags.length > 0 && (
                  <div data-testid="plan-ai-tags">
                    <div className="overline text-accent">Also known for</div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {plan.ai_tags.map((t) => (
                        <span key={t} className="text-xs px-2 py-0.5 bg-accent/10 text-accent border border-accent/30 font-mono">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {Array.isArray(plan.ai_room_schedule) && plan.ai_room_schedule.length > 0 && (
              <div className="mt-12" data-testid="plan-ai-room-schedule">
                <div className="overline text-accent">Room schedule</div>
                <h3 className="font-display text-2xl mt-2 mb-5">Layout at a glance</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 border-t border-border">
                  {plan.ai_room_schedule.map((r, i) => (
                    <div key={i} className="flex items-baseline justify-between gap-3 border-b border-border py-3">
                      <span className="font-medium text-neutral-900">{r.name}</span>
                      {r.notes && <span className="text-xs text-muted-foreground text-right">{r.notes}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* WHAT'S INCLUDED — cards with icons + protected sample previews */}
        <section className="mt-28" data-testid="whats-included">
          <div className="max-w-3xl">
            <div className="overline text-accent">Every purchase includes</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3">What&apos;s included</h2>
            <p className="mt-4 text-muted-foreground text-lg">
              {(plan.deliverables || []).length > 0
                ? <>Delivered with the <span className="font-semibold text-foreground">{PACKAGE_LABEL[selectedTier]} Package</span> — everything you need to build.</>
                : "A complete, ready-to-build package — professionally drafted, watermark-free, and licensed for your build."}
            </p>
          </div>
          {(() => {
            const items = deliverablesForTier(plan, selectedTier);
            if (items.length > 0) {
              // Iter48 — Admin-driven deliverables list. Filtered by selected tier.
              return (
                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="whats-included-list">
                  {items.map((label, i) => (
                    <div key={label} className="border border-border bg-background p-4 flex items-start gap-3 hover:border-accent transition" data-testid={`whats-included-item-${i}`}>
                      <Check size={16} className="text-accent shrink-0 mt-1" />
                      <span className="text-sm">{label}</span>
                    </div>
                  ))}
                </div>
              );
            }
            // Legacy fallback — plans that pre-date Iter48 keep the generic cards.
            return (
              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {WHATS_INCLUDED.map((card, i) => (
                  <div key={card.title} className="border border-border bg-background p-6 hover:border-accent transition group" data-testid={`whats-included-card-${i}`}>
                    <card.Icon size={22} className="text-accent" />
                    <h3 className="mt-4 font-display text-xl">{card.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{card.body}</p>
                  </div>
                ))}
              </div>
            );
          })()}
        </section>

        {/* SAMPLE PLAN SET — dark band */}
        {allImages.length > 0 && (
          <section className="mt-28 -mx-6 lg:-mx-12" data-testid="sample-plan-set">
            <div className="bg-neutral-900 text-white px-6 lg:px-12 py-16 lg:py-20">
              <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="overline text-accent">Preview the full deliverable</div>
                  <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">The sample plan set</h2>
                  <p className="mt-4 text-white/70 text-lg leading-relaxed">
                    Walk through every drawing in the actual set for this plan — floor plans, elevations, sections,
                    roof, foundation, and construction details. Zoom, pan, and inspect the linework at full detail
                    in our protected viewer.
                  </p>
                  <ul className="mt-6 space-y-2 text-sm text-white/70">
                    {sampleCoverage.map((t) => (
                      <li key={t.type} className="flex items-center gap-2" data-testid={`sample-list-${t.type}`}>
                        <Check size={14} className="text-accent" />
                        {t.label} <span className="text-white/40">· {t.count} sheet(s)</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => openLightboxAt(0)}
                    size="lg"
                    className="mt-8 rounded-none bg-accent hover:bg-accent/90 text-neutral-900 h-14 px-8"
                    data-testid="view-sample-plan-set"
                  >
                    <Maximize2 size={16} className="mr-2" /> View Sample Plan Set
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {allImages.slice(0, 4).map((im, i) => (
                    <button
                      key={`${im.url}-${i}`}
                      onClick={() => openLightboxAt(i)}
                      data-testid={`sample-tile-${i}`}
                      className="group relative aspect-[4/3] bg-neutral-800 overflow-hidden border border-white/10 hover:border-accent transition"
                    >
                      <img
                        src={im.url_thumb || im.url}
                        alt={im.label}
                        loading="lazy"
                        decoding="async"
                        draggable={false}
                        onDragStart={stopMediaEvents}
                        onContextMenu={stopMediaEvents}
                        data-protected="true"
                        className={`w-full h-full ${im.fit === "cover" ? "object-cover" : "object-contain p-2"} pointer-events-none`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <div className="absolute bottom-2 left-2 text-[10px] uppercase tracking-widest text-white font-semibold">
                        {DRAWING_TYPE_LABELS[im.type] || im.type}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* WHAT YOU RECEIVE */}
        <section className="mt-28" data-testid="what-you-receive">
          <div className="max-w-3xl">
            <div className="overline text-accent">On checkout</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3">What you receive</h2>
            <p className="mt-4 text-muted-foreground text-lg">
              A clear, honest breakdown of what's in the box today — and what's coming.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {WHAT_YOU_RECEIVE.map((r, i) => {
              const badge = r.status === "included"
                ? { text: "Included", cls: "bg-accent/15 text-accent border-accent/30" }
                : r.status === "on_request"
                ? { text: "On request", cls: "bg-amber-50 text-amber-700 border-amber-200" }
                : { text: "Coming soon", cls: "bg-neutral-100 text-neutral-500 border-neutral-300" };
              return (
                <div key={r.title} className="border border-border bg-background p-5" data-testid={`what-you-receive-${i}`}>
                  <r.Icon size={20} className="text-accent" />
                  <h3 className="mt-3 font-semibold text-base">{r.title}</h3>
                  <span className={`inline-block mt-2 text-[10px] uppercase tracking-widest border rounded px-1.5 py-0.5 font-semibold ${badge.cls}`}>{badge.text}</span>
                  <p className="mt-3 text-xs text-muted-foreground leading-relaxed">{r.note}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* LICENSE & USAGE */}
        <section className="mt-28" data-testid="license-usage">
          <div className="max-w-3xl">
            <div className="overline text-accent">The fine print, in plain English</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3">License &amp; usage</h2>
          </div>
          <div className="mt-10 border border-border divide-y divide-border">
            {LICENSE_ROWS.map((row, i) => (
              <div key={row.title} className="flex items-start gap-4 p-5 md:p-6 hover:bg-muted/40 transition" data-testid={`license-row-${i}`}>
                <div className="p-2.5 rounded-full bg-accent/10 text-accent flex-shrink-0">
                  <row.Icon size={16} />
                </div>
                <div>
                  <h3 className="font-semibold">{row.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{row.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CUSTOM MODIFICATIONS CTA */}
        <section className="mt-28 -mx-6 lg:-mx-12" data-testid="custom-mod-cta">
          <div className="bg-accent/10 border-y border-accent/30 px-6 lg:px-12 py-16">
            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <div className="overline text-accent">Need this plan tuned to your lot?</div>
                <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
                  We'll tailor <em className="not-italic text-accent">{plan.title}</em> to your family, your climate, and your code.
                </h2>
                <p className="mt-4 text-muted-foreground max-w-2xl text-lg leading-relaxed">
                  Mirror the layout, extend the master, adapt for tropical vs cold climate, adjust the garage, or drop a
                  wing entirely — every modification is priced up front, quoted within 24 hours, and delivered by
                  the same team that drew the original.
                </p>
              </div>
              <div className="flex flex-col gap-3 md:items-end">
                <Button
                  onClick={handleRequestCustom}
                  size="lg"
                  className="rounded-none bg-neutral-900 hover:bg-black text-white h-14 px-8"
                  data-testid="custom-mod-request-btn"
                >
                  <MessageSquare size={16} className="mr-2" /> Request Customization
                </Button>
                <Link to="/custom-design" className="text-sm text-accent hover:underline flex items-center gap-1" data-testid="custom-design-link">
                  Or learn about our Custom Design service <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="faq-section">
          <div className="lg:col-span-4">
            <div className="overline text-accent">Buying &amp; building, answered</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3">FAQ</h2>
            <p className="mt-4 text-muted-foreground">
              Everything customers ask before hitting Buy. Not seeing yours? {" "}
              <button onClick={handleRequestCustom} className="text-accent underline">Ask our team →</button>
            </p>
          </div>
          <div className="lg:col-span-8 divide-y divide-border border-y border-border">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={f.q} data-testid={`faq-item-${i}`}>
                  <button
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 py-5 text-left"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-semibold text-lg">{f.q}</span>
                    {open ? <ChevronUp size={20} className="text-accent shrink-0" /> : <ChevronDown size={20} className="text-muted-foreground shrink-0" />}
                  </button>
                  {open && (
                    <div className="pb-6 text-muted-foreground leading-relaxed" data-testid={`faq-answer-${i}`}>
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* RELATED PLANS */}
        {related.length > 0 && (
          <section className="mt-28" data-testid="related-plans">
            <div className="flex items-baseline justify-between mb-8">
              <div>
                <div className="overline text-accent">You may also like</div>
                <h2 className="font-display text-4xl md:text-5xl mt-3">Related plans</h2>
              </div>
              <Link to="/plans" className="text-sm text-accent hover:underline hidden md:flex items-center gap-1" data-testid="see-all-plans">
                See all plans <ArrowRight size={12} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="related-plans-grid">
              {related.map((p) => (
                <PlanCard key={p.id} plan={p} />
              ))}
            </div>
          </section>
        )}
      </div>

      <ProtectedLightbox
        images={allImages.filter((m) => !m.isVideo)}
        initialIndex={lightboxStart}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        purchaseCta={purchaseCta}
      />
    </Layout>
  );
};

const Spec = ({ icon: Icon, value, label }) => (
  <div className="flex items-center gap-3">
    <Icon size={20} className="text-accent" />
    <div>
      <div className="font-semibold">{value ?? "—"}</div>
      <div className="overline text-muted-foreground">{label}</div>
    </div>
  </div>
);

export default PlanDetail;
